import * as Sentry from '@sentry/react';
import React, { useRef, useState } from 'react';

import Button from '@/componentsv2/Button';
import Checkbox from '@/componentsv2/Checkbox';
import Interstitial from '@/componentsv2/Interstitial';
import config from '@/config';
import {
  EPA_EFFECTIVE_DATE,
  EPA_OPT_OUT_DEADLINE,
  REQUIRED_EPA_VERSION,
  policies,
} from '@/core/agreements';
import type { Agreement } from '@/core/agreements';
import { isGatewayFailure, isWireLevelFailure } from '@/core/api';
import { isGateExempt } from '@/routes/routesMiddleware';
import { useAppDispatch, useAppSelector } from '@/store';
import { updateUser } from '@/store/user';
import { sameOriginPath } from '@/utils/sameOriginPath';

const EPA_KEY = 'expert-participation-agreement';

// The plain-language summary of the Rev 2.1 changes, on the support site.
// Brian owns the article; the slug is fixed even though publication is pending,
// so this can land now. Rendered only when the article is live, per the ticket:
// a broken link on a legal consent surface is worse than no link.
const SUMMARY_URL =
  'https://support.onfrontiers.com/help-center/standard-terms-update-august-2026/';
const SUMMARY_PUBLISHED = true;

// The opt-out address per EPA 15.7, and the address to write to when
// acceptance itself will not go through. Plain selectable text rather than a
// mailto, per Brian: mailto: fails silently when no mail client is configured,
// and the address is the instruction either way.
const CONTACT_ADDRESS = 'hello@onfrontiers.com';

// Three messages, because the expert's next move differs in each case, and
// only one of them is about their account.
//
// `config.productName` rather than a literal: Interstitial renders the tenant's
// own logo, so a white-label expert would otherwise be told we cannot reach a
// brand that is nowhere on their screen. The legal copy below stays "OnFrontiers"
// deliberately — that is the contracting entity and must not be templated.
//
// "try again" rather than "tap Continue again": the Sentry report came from one
// Android user, but the gated population is ~55k and mostly on desktop.
const CONNECTION_ERROR = `We could not reach ${config.productName}. Check your connection and try again.`;
// A gateway shed the request, which happens while the API rolls out a revision.
// Nothing is wrong with the acceptance and nothing is wrong with their network,
// so blaming either would be a lie that generates a support ticket.
const UNAVAILABLE_ERROR = `${config.productName} is temporarily unavailable. Please try again in a moment.`;
// Anything else survived the transport retries and a real server round trip, so
// this is the one case where trying again may never work — the go-live shape
// where the api lacks the version string, for instance. The surface has no nav
// and no way past it, so "please try again" alone is a dead end. The subject
// line is not decoration: CONTACT_ADDRESS is also the arbitration opt-out
// address, and a genuine opt-out mis-triaged as a stuck button costs the expert
// a legal right they believe they exercised.
const RECORDING_ERROR = `We could not record your acceptance. Please try again — if this keeps happening, email ${CONTACT_ADDRESS} with the subject "Cannot accept EPA".`;

/**
 * A stalled connection is the one failure the catch below would never see.
 * fetch has no timeout, so a request that is neither answered nor refused
 * leaves Continue spinning until the browser gives up on its own — around 90
 * seconds on Firefox for Android, on a screen that blocks the whole product and
 * offers nothing else to do. Bound it here and let the expert try again.
 *
 * Generous on purpose: a slow-but-working connection must not be cut off
 * mid-write. RetryLink makes at most 3 attempts (the original plus 2 retries)
 * and its jittered backoff tops out around 1.8s in total, so the transport
 * spends ~6% of this budget at worst and the rest belongs to the request.
 */
const ACCEPTANCE_TIMEOUT_MS = 30_000;

class AcceptanceTimeout extends Error {}

// Kept separate from the messages so the Sentry taxonomy and the copy cannot
// drift apart: `timeout` and `wire` are both the connection, but they mean
// different things to whoever is on call.
type FailureKind = 'timeout' | 'wire' | 'unavailable' | 'server';

const FAILURE_MESSAGES: Record<FailureKind, string> = {
  timeout: CONNECTION_ERROR,
  wire: CONNECTION_ERROR,
  unavailable: UNAVAILABLE_ERROR,
  server: RECORDING_ERROR,
};

function classifyFailure(e: unknown): FailureKind {
  if (e instanceof AcceptanceTimeout) return 'timeout';
  if (isWireLevelFailure(e)) return 'wire';
  if (isGatewayFailure(e)) return 'unavailable';
  return 'server';
}

/**
 * Rejects at the deadline AND aborts the work, rather than walking away from a
 * request that is still running.
 *
 * Abandoning it without aborting would re-enable Continue while a write was
 * still in flight, so one more tap put two acceptances on the wire at once —
 * survivable here only because the mutation is replay-safe, which is a property
 * of the server this component should not be leaning on. Aborting also frees a
 * socket on an already-degraded connection and stops RetryLink, and it makes
 * CONNECTION_ERROR true rather than merely probable: without it a request that
 * landed at 31s left the expert reading that we could not be reached while a
 * binding acceptance sat on the server.
 */
function withTimeout<T>(start: (signal: AbortSignal) => Promise<T>, ms: number): Promise<T> {
  const controller = new AbortController();
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.abort();
      reject(new AcceptanceTimeout(`acceptance did not complete within ${ms}ms`));
    }, ms);
    // The abort makes `start` reject too, but that lands on an already-settled
    // promise and is handled here, so it cannot surface as an unhandled
    // rejection.
    start(controller.signal)
      .then(resolve, reject)
      .finally(() => clearTimeout(timer));
  });
}

const CHANGES = [
  {
    title: 'One agreement, three ways of working',
    body: 'Video Consultations, Written Responses and Scoped Engagements, with clear payment rules for each.',
  },
  {
    title: 'More control over your contributions than before',
    body: 'You retain ownership of your work; OnFrontiers takes an exclusive license to build products and services from platform content; customers’ rights are scoped to what they elect and pay for; your expertise, methods, and general knowledge always remain yours.',
  },
  {
    title: 'Attribution is your choice',
    body: 'For Library content, be named or listed by professional title. Title is the default.',
  },
  {
    title: 'AI-training transparency',
    body: 'Your work is never provided to an outside party for AI training unless that is stated on the order before you accept it.',
  },
  {
    title: 'Disputes',
    body: `The agreement now includes arbitration with class-action and jury waivers. You can opt out by ${EPA_OPT_OUT_DEADLINE}, see below.`,
  },
];

const PolicyLink = ({ href, children }: { href: string; children: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-brand-primary hover:text-brand-primary-hover"
  >
    {children}
  </a>
);

const AgreementAcceptance = (): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const viewer = useAppSelector((state) => state.viewer);

  // Union because componentsv2/Button forwards to either element; only focus()
  // is used here, which both provide.
  const continueRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const epa = policies[EPA_KEY];
  const privacy = policies.privacy;
  const paymentPolicy = policies['expert-payment-policy'];

  // The exact string the gate requires, not policies[EPA].apiKey. Those are two
  // sources for one value, and if they ever disagree the write succeeds, the
  // confirmation below passes, and the middleware bounces the expert straight
  // back here forever. Non-null whenever this screen renders, since
  // agreementGateOnlyMiddleware turns everyone else away, but fall back rather
  // than write `null`.
  const policy = REQUIRED_EPA_VERSION ?? epa.apiKey;

  const handleContinue = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const now = new Date().toISOString();

      // Send only the agreement being accepted. updateUser merges rather than
      // replaces: it loops the input and calls UpdateAgreement per entry, which
      // find-or-appends against the stored list.
      //
      // Resending the untouched entries actively breaks: UpdateAgreement
      // rejects any policy string absent from compliancesRulesMap, and records
      // written before mid-2023 carry unversioned strings ("terms-of-use"
      // rather than "terms-of-use:6848") that are not in that map. There is no
      // transaction, so the first rejection aborts before the EPA is written.
      const accepted = { policy, accepted: true, updated_at: now };

      const updated = await withTimeout(
        (signal) => dispatch(updateUser({ id: viewer.id, agreements: [accepted] }, { signal })),
        ACCEPTANCE_TIMEOUT_MS
      );

      // updateUser resolves undefined when the mutation returns falsy data, and
      // data.updateUser can itself be null. Neither throws. Treating that as
      // success and navigating away would send the expert into a permanent
      // loop: the reload refetches the viewer, the server still has no
      // acceptance, and the gate returns them here with nothing on screen
      // explaining why. Insist on the server's own copy instead.
      const nextAgreements: Agreement[] | undefined = updated?.agreements;
      if (!nextAgreements?.some((a) => a?.policy === policy && a.accepted)) {
        throw new Error('acceptance was not recorded server-side');
      }

      // Full reload rather than a router navigate: the gate blocked every
      // authenticated route, so nothing behind it has mounted or fetched. That
      // also refetches the viewer from the server on boot, which is why no
      // setViewer is needed here.
      window.location.assign(nextPath());
    } catch (e) {
      // Anything reaching here has already exhausted RetryLink's attempts, so
      // a wire-level failure means the connection is genuinely gone rather
      // than momentarily flaky.
      const failure = classifyFailure(e);

      setSubmitting(false);
      setError(FAILURE_MESSAGES[failure]);
      // Returning focus to the control the expert was using. Disabling the
      // button blurs it natively, which drops a keyboard or screen-reader user
      // onto <body> on a surface with nothing else to tab to.
      continueRef.current?.focus();
      // Reported explicitly. The Apollo chain's SentryLink only attaches
      // breadcrumbs and operation context to events raised elsewhere; it does
      // not create them, and this catch swallows the throw. Without this, a
      // go-live where the api lacks the new version string in compliancesRulesMap
      // fails for every active expert and shows up nowhere but support tickets.
      //
      // The kind goes in `tags`, not `extra`, because the whole point is to be
      // able to tell these apart — and `extra` is not searchable, alertable or
      // groupable. A spike in `server` is ours to fix right now; a steady
      // trickle of `wire` is the mobile internet and must not bury it, which is
      // what the level split is for (Sentry WEBAPP-CCG).
      Sentry.captureException(e, {
        level: failure === 'server' ? 'error' : 'warning',
        tags: { feature: 'eng-5570-epa-gate', epa_failure: failure },
        extra: {
          policy,
          viewerId: viewer.id,
          ...(failure === 'timeout' && { timedOutAfterMs: ACCEPTANCE_TIMEOUT_MS }),
        },
      });
    }
  };

  return (
    <Interstitial
      documentTitle="Updated Expert Participation Agreement"
      footer={
        <p>
          You may opt out of the arbitration provision by {EPA_OPT_OUT_DEADLINE}, or within 30 days
          of your acceptance if that is later, by emailing{' '}
          <span className="select-all">{CONTACT_ADDRESS}</span>. Opting out does not affect your
          standing on the Platform in any way.
        </p>
      }
    >
      <h1 className="mb-8 text-24 font-semibold leading-tight text-on-surface">
        We&rsquo;ve updated the Expert Participation Agreement
      </h1>
      <p className="mb-32 text-14 text-on-surface-secondary">
        {EPA_EFFECTIVE_DATE ? <span>Effective {EPA_EFFECTIVE_DATE}. </span> : null}
        <span>Here&rsquo;s what&rsquo;s changing:</span>
      </p>

      <ul className="mb-32 flex list-none flex-col gap-16">
        {CHANGES.map((change) => (
          <li key={change.title}>
            <span className="block text-14 font-semibold text-on-surface">{change.title}</span>
            <span className="text-14 text-on-surface-muted">{change.body}</span>
          </li>
        ))}
      </ul>

      <p className="mb-8 text-14">
        <PolicyLink href={epa.url}>Expert Participation Agreement</PolicyLink>
        <span className="px-6 text-on-surface-subtle">&middot;</span>
        <PolicyLink href={paymentPolicy.url}>Expert Payment Policy</PolicyLink>
        {SUMMARY_PUBLISHED ? (
          <>
            <span className="px-6 text-on-surface-subtle">&middot;</span>
            <PolicyLink href={SUMMARY_URL}>Summary of changes</PolicyLink>
          </>
        ) : null}
      </p>
      <p className="mb-32 text-14 text-on-surface-muted">
        Our Privacy Policy has also been updated.{' '}
        <PolicyLink href={privacy.url}>Read it</PolicyLink>.
      </p>

      <div className="mb-24 rounded-md border border-surface-border bg-surface-elevated px-18 py-14">
        <Checkbox
          checked={agreed}
          onChange={(_, checked) => setAgreed(checked)}
          disabled={submitting}
          inputProps={{ 'aria-describedby': 'epa-continue-hint' }}
          label={
            <span className="text-14 font-medium text-on-surface">
              I have reviewed and agree to the updated Expert Participation Agreement.
            </span>
          }
        />
        {/* Directly under the control it describes, so visual order matches the
            aria-describedby association. text-on-surface-subtle fails AA in both
            themes, and this is the one instruction an expert needs to get past a
            gate that blocks every authenticated route. */}
        <p id="epa-continue-hint" className="mt-8 text-12 text-on-surface-muted">
          {agreed ? ' ' : 'Tick the box above to continue.'}
        </p>
      </div>

      {error ? (
        <p role="alert" className="mb-16 text-14 text-destructive-primary dark:text-red-400">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-24">
        <Button
          ref={continueRef}
          onClick={handleContinue}
          disabled={!agreed || submitting}
          loading={submitting}
          aria-busy={submitting}
        >
          Continue
        </Button>
        <a
          href="/logout"
          className="inline-flex min-h-48 items-center text-14 text-on-surface-muted hover:text-on-surface"
        >
          Log out
        </a>
      </div>
    </Interstitial>
  );
};

/**
 * The middleware appends ?next= when it redirects here. Same-origin only: an
 * unvalidated value is an open redirect one click after a legal acceptance.
 */
function nextPath(): string {
  const path = sameOriginPath(new URLSearchParams(window.location.search).get('next'));
  // Never bounce back to the gate or another exempt surface.
  if (!path || isGateExempt(new URL(path, window.location.origin).pathname)) return '/dashboard';
  return path;
}

export default AgreementAcceptance;
